import {
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Box,
} from "@mui/material";
import { useEffect, type FC } from "react";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useRequest } from "../../contexts/RequestProvider";
import { CheckboxIcon } from "@shared/frontend/icons";
import { compareBySortOrder } from "@shared/helpers/category-sort";

type CategorySelection = {
  id?: string;
  categoryId?: string;
  name?: string;
};

function selectedCategoryId(value: CategorySelection | string): string {
  if (typeof value === "string") return value;
  return value.categoryId || value.id || "";
}

function selectedCategoryName(
  value: CategorySelection | string,
  catalog: { id: string; name: string }[],
): string {
  if (typeof value !== "string" && value.name?.trim()) return value.name.trim();
  const id = selectedCategoryId(value);
  return catalog.find((category) => category.id === id)?.name?.trim() ?? "";
}

export const CategorySelect: FC = () => {
  const { currOrganization } = useOrganization();
  const { currentEvent, setCurrentEvent } = useRequest();

  const categories = (currOrganization?.categories || [])
    .slice()
    .sort(compareBySortOrder);
  const selectedCategories = (currentEvent.selectedCategories || []) as (
    | CategorySelection
    | string
  )[];
  const catalogIds = new Set(categories.map((category) => category.id));
  const catalogKey = categories.map((category) => category.id).join(",");
  const selectedIds = selectedCategories
    .map((category) => selectedCategoryId(category))
    .filter((id) => catalogIds.has(id));

  useEffect(() => {
    if (!catalogKey) return;
    if (selectedIds.length === selectedCategories.length) return;
    const kept = categories.filter((category) =>
      selectedIds.includes(category.id),
    );
    setCurrentEvent((prev) => ({ ...prev, selectedCategories: kept }));
  }, [catalogKey, selectedIds.join(","), selectedCategories.length]);

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    const nextIds = typeof value === "string" ? value.split(",") : value;
    const selected = categories.filter((category) =>
      nextIds.includes(category.id),
    );
    setCurrentEvent((prev) => ({ ...prev, selectedCategories: selected }));
  };

  if (!currOrganization) return null;

  return (
    <Stack className="field-container">
      <label className="field-label">קטגוריות</label>
      <Select
        multiple
        slotProps={{
          root: { className: "field" },
        }}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: {
            sx: {
              borderRadius: "20px",
            },
          },
        }}
        value={selectedIds}
        onChange={handleChange}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(selected as string[]).map((id) => {
              const match = selectedCategories.find(
                (category) => selectedCategoryId(category) === id,
              );
              const label = match
                ? selectedCategoryName(match, categories)
                : categories.find((category) => category.id === id)?.name?.trim() ??
                  "";
              if (!label) return null;
              return <Chip key={id} label={label} size="small" />;
            })}
          </Box>
        )}
      >
        {categories.map((category) => (
          <MenuItem key={category.id} value={category.id}>
            <Checkbox
              checkedIcon={<CheckboxIcon checked />}
              icon={<CheckboxIcon />}
              checked={selectedIds.includes(category.id)}
            />
            <ListItemText primary={category.name} />
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};
